import {
  Alert,
  Box,
  BoxContent,
  BoxHeading,
  Link,
  TextLink,
} from '@mm/components';
import { CloseCircleIcon, QuestionCircleIcon, InfoIcon } from '@mm/icons';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { useIceBreakerCRUDActions } from '@mm/state/actions/IceBreakerCRUDActions';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import HeroImage from 'assets/hero-image.png';
import { ReactComponent as Pointer } from 'assets/pointer.svg';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Label } from 'reactstrap';
import { PrimaryToolTip } from '../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { BotBuilderRoutes } from '../../../routes/bot-builder-routes';
import { colors } from '../beta/styles/colors';
import { InactiveChannelAlertSmart } from '../campaigns/panels/DripCampaignDetailsPanel/InactiveChannelAlert';
import { NeedHelpLabel } from '../common/components/NeedHelpLabel/NeedHelpLabel';
import { SwitchStatus } from '../common/components/SwitchStatus/SwitchStatus';
import { FacebookPagePreview } from './FacebookPagePreview/FacebookPagePreview';
import { FacebookPageWelcomerNew } from './FacebookPageWelcomerNew/FacebookPageWelcomerNew';
import './FacebookPageWelcomerOverview.pcss';
import { IceBreaker } from './IceBreaker/IceBreaker';
import { IceBreakerPreview } from './IceBreaker/IceBreakerPreview/IceBreakerPreview';
import { UserRoleContainer } from '../../../common/hooks/use-route-guard';

type Props = {
  className?: string;
};

export const FacebookPageWelcomerOverview = ({ className }: Props) => {
  const [alertVisible, setAlertVisible] = useState(true);
  const bot = useSelector(activeChatbotSelector);
  const chatbotId = bot.id;
  const { updateUserAttributes } = useUsersCRUDActions();
  const { userAttributes, id: userId } = useSelector(selectSessionSelector);
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const [showDescription, setShowDescription] = useState(
    !userAttributes?.hideWelcomerDescription,
  );
  const hideDescription = () => {
    setShowDescription(false);
    if (userAttributes?.activeCompanyId) {
      updateUserAttributes(userAttributes.activeCompanyId, userId, {
        hideWelcomerDescription: true,
      });
    }
  };
  const onStatusChanged = () => {
    botCRUDActions.update({
      id: chatbotId,
      fbWelcomerDisabled: !bot.fbWelcomerDisabled,
    });
  };
  const {
    roleFlags: { isCollaborator, isFacebookPageCollaborator },
  } = UserRoleContainer.useContainer();
  const connectedPageCRUDActions = useConnectedPageCRUDActions();
  const iceBreaker = useSelector(
    (state: StoreState) => state.iceBreakers.facebook.entities[0],
  );
  const botCRUDActions = useChatbotCRUDActions();
  const iceBreakerCRUDActions = useIceBreakerCRUDActions();
  useEffect(() => {
    if (iceBreaker?.botId !== chatbotId) {
      iceBreakerCRUDActions.retrieveAll(chatbotId, 'facebook');
    }
  }, [chatbotId]);
  const closeAlert = () => setAlertVisible(false);
  const activeKind = iceBreaker?.active
    ? 'icebreakers'
    : 'facebook_page_welcomer';
  const link =
    activeKind === 'facebook_page_welcomer'
      ? 'https://customers.ai/help/article/yjkeatbg5d-facebook-page-welcomer'
      : 'https://customers.ai/help/article/zioq7mndcr-ice-breakers';

  return (
    <section className={classNames(className, 'welcomer')}>
      <InactiveChannelAlertSmart facebook type="Page Welcomer" />

      <BoxHeading
        className="welcomer__bot-heading"
        backLink={BotBuilderRoutes.chatStarters.getLink({ chatbotId })}
      >
        <span className="welcomer__bot-heading-text">
          Facebook Page Welcomer
        </span>
        <div className="welcomer__bot-heading-right">
          <NeedHelpLabel link={link} />
          <SwitchStatus
            active={!bot.fbWelcomerDisabled}
            disabled={false}
            onChange={onStatusChanged}
          />
        </div>
      </BoxHeading>
      <Box className="welcomer__box">
        <BoxContent className="welcomer__content">
          <div className="welcomer__content-data">
            {showDescription && (
              <div className="welcomer__description">
                {activeFacebookPage && (
                  <FacebookPagePreview facebookPage={activeFacebookPage} />
                )}
                {!activeFacebookPage && (
                  <img
                    src={HeroImage}
                    className="welcomer__hero-img"
                    alt="Facebook Hero"
                  />
                )}
                <div className="welcomer__description-text">
                  <span>
                    Create a Dialogue that will interact with contacts who
                    started a conversation from your Facebook page.
                    Conversations are started when the contact{' '}
                    <b>clicks on the "Send Message"</b> button on your page.
                  </span>
                  <Pointer className="welcomer__description-pointer" />
                </div>
                <div
                  className="welcomer__close-description"
                  onClick={hideDescription}
                >
                  <CloseCircleIcon
                    size={22}
                    color="#FFF"
                    className="welcomer__close-description-circle"
                  />
                  <span>Close</span>
                </div>
              </div>
            )}
            <div className="welcomer__type-selectors-container">
              <div className="welcomer__type-selector-wrapper">
                <input
                  type="radio"
                  id="use-get-started-checker"
                  className="welcomer__radio-input"
                  checked={activeKind === 'facebook_page_welcomer'}
                  onChange={() => {
                    iceBreakerCRUDActions.update(iceBreaker.botId, {
                      id: iceBreaker.id,
                      active: false,
                    });
                  }}
                />
                <Label
                  className="welcomer__radio-label"
                  for="use-get-started-checker"
                >
                  Show Get Started Button
                </Label>
                <PrimaryToolTip
                  classNameContent="welcomer__type-selector-tooltip-content"
                  classNamePopoverContainer="welcomer__tooltip-popover"
                  anchorElement={
                    <QuestionCircleIcon
                      className="welcomer__type-selector-question-icon"
                      color={colors.accentBlue}
                    />
                  }
                >
                  If you choose this option, users will see a Get Started
                  button. When they click on this button the following chat
                  dialogue will be triggered.
                </PrimaryToolTip>
              </div>
              <div className="welcomer__type-selector-wrapper">
                <input
                  type="radio"
                  id="use-icebreakers-checker"
                  className="welcomer__radio-input"
                  checked={activeKind === 'icebreakers'}
                  onChange={() => {
                    iceBreakerCRUDActions.update(iceBreaker.botId, {
                      id: iceBreaker.id,
                      active: true,
                    });
                  }}
                />
                <Label
                  className="welcomer__radio-label"
                  for="use-icebreakers-checker"
                >
                  Show Icebreakers
                </Label>
                <div className="welcomer__tooltip-container">
                  <PrimaryToolTip
                    classNameContent="welcomer__type-selector-tooltip-content"
                    classNamePopoverContainer="welcomer__tooltip-popover"
                    anchorElement={
                      <QuestionCircleIcon
                        className="welcomer__type-selector-question-icon"
                        color={colors.accentBlue}
                      />
                    }
                  >
                    Icebreakers are pre-populated Frequently Asked Questions
                    that customers can click on to start a conversation with
                    your Facebook page. Use this option if your customers
                    frequently ask the same questions.
                  </PrimaryToolTip>
                </div>
              </div>
            </div>
            {activeKind === 'facebook_page_welcomer' && (
              <FacebookPageWelcomerNew />
            )}
            {activeKind === 'icebreakers' && (
              <div className="welcomer__ice-breaker">
                <div className="welcomer__ice-breaker-container">
                  {!isCollaborator && !isFacebookPageCollaborator && (
                    <div className="welcomer__ice-breaker-info-message">
                      <InfoIcon className="welcomer__ice-breaker-info-icon" />
                      {activeFacebookPage && (
                        <TextLink
                          className="welcomer__ice-breaker-text-link"
                          onClick={() => {
                            connectedPageCRUDActions.refreshAccessToken(
                              activeFacebookPage.id,
                            );
                          }}
                        >
                          Ice breakers not showing on Facebook? Click here to
                          Refresh token
                        </TextLink>
                      )}
                    </div>
                  )}
                  {alertVisible && (
                    <Alert
                      className="welcomer__trial-alert"
                      type="warning"
                      closeIconProps={{ size: 18, onClick: closeAlert }}
                    >
                      <div>
                        <span>
                          <b>IMPORTANT:</b> Share links will NOT work for new
                          contacts if you use icebreakers in your welcomer.
                          Learn more about Share Links
                        </span>{' '}
                        <Link
                          href="https://customers.ai/help/article/9elggt28na-share-links"
                          className="welcomer__alert-action"
                          isBlank
                        >
                          here
                        </Link>
                      </div>
                    </Alert>
                  )}
                  <IceBreaker iceBreaker={iceBreaker} showHeading={false} />
                </div>
                <div className="welcomer__ice-breaker-preview">
                  <IceBreakerPreview iceBreaker={iceBreaker} />
                </div>
              </div>
            )}
          </div>
        </BoxContent>
      </Box>
    </section>
  );
};
